import * as React from "react";

import { cn } from "@/lib/utils";

export interface InputProps extends React.ComponentProps<"input"> {
  /** Optional leading icon (rendered inside the input on the left). */
  leadingIcon?: React.ReactNode;
  /** Optional trailing element (e.g. password eye toggle). */
  trailing?: React.ReactNode;
  /**
   * Visual variant.
   * - "default": h-10, surface background. Standard forms.
   * - "glass": h-10, translucent background + backdrop-blur — for forms posed on glass surfaces.
   * - "premium": h-11, rounded-xl, ring-4 — for marketing / auth pages.
   */
  variant?: "default" | "glass" | "premium";
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, leadingIcon, trailing, variant = "default", "aria-invalid": ariaInvalid, ...props }, ref) => {
    // Transition scoped to colors only — `transition-all` rendait chaque focus /
    // blur visible sur ~200ms (border, ring, bg, shadow, transform, padding…)
    // ce qui se ressent comme une latence à la saisie / au tab.
    // Saisie en text-foreground : le placeholder (gris à 85 %) doit rester distinct.
    // La couleur précède text-payfit-body : écrite après, cn() effacerait l'utilitaire.
    const baseShared =
      "flex w-full text-foreground text-payfit-body transition-colors duration-fast file:border-0 file:bg-transparent file:text-payfit-label file:text-foreground placeholder:text-payfit-placeholder focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50";

    let baseClasses: string;

    if (variant === "premium") {
      baseClasses = cn(
        baseShared,
        "h-11 rounded-xl border-2 border-input bg-background px-3.5 py-2",
        "focus:border-primary focus:ring-4 focus:ring-primary/15 focus:bg-primary/[0.02]",
        ariaInvalid && "border-destructive focus:border-destructive focus:ring-destructive/15",
      );
    } else if (variant === "glass") {
      baseClasses = cn(
        baseShared,
        "h-10 rounded-md border border-foreground/10 bg-background/60 backdrop-blur-sm px-3 py-2",
        "focus:border-primary focus:ring-2 focus:ring-primary/15",
        ariaInvalid && "border-destructive focus:border-destructive focus:ring-destructive/15",
      );
    } else {
      baseClasses = cn(
        baseShared,
        "h-10 rounded-md border border-input bg-background px-3 py-2",
        "focus:border-primary focus:ring-2 focus:ring-primary/15",
        ariaInvalid && "border-destructive focus:border-destructive focus:ring-destructive/15",
      );
    }

    baseClasses = cn(baseClasses, leadingIcon && "pl-10", trailing && "pr-10");

    const inputEl = (
      <input type={type} className={cn(baseClasses, className)} ref={ref} aria-invalid={ariaInvalid} {...props} />
    );

    if (!leadingIcon && !trailing) return inputEl;

    return (
      <div className="relative w-full">
        {leadingIcon && (
          <span
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 flex items-center justify-center text-muted-foreground [&_svg]:w-4 [&_svg]:h-4"
            aria-hidden="true"
          >
            {leadingIcon}
          </span>
        )}
        {inputEl}
        {trailing && (
          <span className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center">
            {trailing}
          </span>
        )}
      </div>
    );
  },
);
Input.displayName = "Input";

export { Input };
